import { emitUnauthorizedLogout } from '../utils/authEvents';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface SiteNameAvailabilityRequest {
  site_name: string;
}

export interface SiteNameAvailabilityResponse {
  available: boolean;
  message: string;
  site_name: string;
}

export interface CreateOrderRequest {
  country: string;
  country_code: string;
  flag: string;
  selected_plan: string;
  selected_plan_id: string;
  site_name: string;
  theme?: string;
  otp_code?: string;
  notes?: string;
  total_amount?: number;
  currency?: string;
  payment_network_id?: string;
  payment_network_name?: string;
  payment_network_symbol?: string;
  payment_network_chain?: string;
  payment_wallet_address?: string;
  payment_tx_hash?: string;
}

export interface UpdateOrderRequest {
  status?: string;
  payment_status?: string;
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
  payment_network_id?: string;
  payment_network_name?: string;
  payment_network_symbol?: string;
  payment_network_chain?: string;
  payment_wallet_address?: string;
  payment_tx_hash?: string;
}

export interface BusinessOrder {
  id: string;
  user_id: string;
  country: string;
  country_code: string;
  flag: string;
  selected_plan: string;
  selected_plan_id: string;
  site_name: string;
  theme?: string;
  otp_code?: string;
  order_id: string;
  status: string;
  total_amount: number;
  currency: string;
  payment_status: string;
  payment_method?: string;
  payment_reference?: string;
  payment_network_id?: string;
  payment_network_name?: string;
  payment_network_symbol?: string;
  payment_network_chain?: string;
  payment_wallet_address?: string;
  payment_tx_hash?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface OrderResponse {
  order: BusinessOrder;
}

export interface OrdersResponse {
  orders: BusinessOrder[];
  total: number;
  page: number;
  limit: number;
}

export interface CryptoPaymentNetwork {
  id: string;
  name: string;
  symbol: string;
  network: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CryptoPaymentAddress {
  id: string;
  network_id: string;
  address: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CryptoPaymentTip {
  id: string;
  network_id: string;
  amount: string;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CryptoPaymentDetailsResponse {
  networks: CryptoPaymentNetwork[];
  addresses: CryptoPaymentAddress[];
  tips: CryptoPaymentTip[];
}

const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

const createFetchOptions = (method: string = 'GET', body?: any): RequestInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

const handleUnauthorized = (status: number) => {
  if (status === 401) {
    emitUnauthorizedLogout();
  }
};

export const businessApi = {
  async checkSiteNameAvailability(
    data: SiteNameAvailabilityRequest
  ): Promise<ApiResponse<SiteNameAvailabilityResponse>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/auth/check-site-name`,
        createFetchOptions('POST', data)
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to check site name availability',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: responseData.message,
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to check site name availability',
        error: error?.message || 'Unknown error',
      };
    }
  },

  async createOrder(
    data: CreateOrderRequest
  ): Promise<ApiResponse<OrderResponse>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/platform/orders`,
        createFetchOptions('POST', data)
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to create order',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: 'Order created successfully',
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to create order',
        error: error?.message || 'Unknown error',
      };
    }
  },

  async getUserOrders(
    page: number = 1,
    limit: number = 10
  ): Promise<ApiResponse<OrdersResponse>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/platform/orders?page=${page}&limit=${limit}`,
        createFetchOptions('GET')
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to fetch orders',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: 'Orders fetched successfully',
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to fetch orders',
        error: error?.message || 'Unknown error',
      };
    }
  },

  async updateOrder(
    orderId: string,
    data: UpdateOrderRequest
  ): Promise<ApiResponse<OrderResponse>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/platform/orders/${orderId}`,
        createFetchOptions('PUT', data)
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to update order',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: 'Order updated successfully',
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to update order',
        error: error?.message || 'Unknown error',
      };
    }
  },

  async getActivePackages(): Promise<ApiResponse<any[]>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/packages`,
        createFetchOptions('GET')
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to get packages',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: 'Packages retrieved successfully',
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to get packages',
        error: error?.message || 'Unknown error',
      };
    }
  },

  async getCompletePaymentDetails(): Promise<ApiResponse<CryptoPaymentDetailsResponse>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/crypto/payment-details`,
        createFetchOptions('GET')
      );

      const responseData = await response.json();

      if (!response.ok) {
        handleUnauthorized(response.status);
        return {
          success: false,
          message: responseData.error || 'Failed to get payment details',
          error: responseData.error || 'Unknown error',
        };
      }

      return {
        success: true,
        message: 'Payment details retrieved successfully',
        data: responseData,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to get payment details',
        error: error?.message || 'Unknown error',
      };
    }
  },
};

